/**
 * What to tell a reader whose dashboard did not load.
 *
 * One message covered a 404, a 403 and a network failure alike, which made a permission problem
 * read as a bad link. Those need different words and different actions: a retry is the right offer
 * for a network failure and useless for either of the other two, and telling somebody to check
 * their link when the real answer is that nobody shared the dashboard with them sends them looking
 * in the wrong place.
 *
 * Private Lenses need reporting-admin access or an explicit share, so being refused a
 * dashboard somebody else owns is an ordinary event here rather than an edge case.
 */
export type LensLoadError = {
  message: string;
  /** False where retrying cannot change the outcome. */
  canRetry: boolean;
};

/** Reads the HTTP status off whatever the request layer threw, if it carried one. */
function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

export function describeLensLoadError(error: unknown): LensLoadError {
  switch (statusOf(error)) {
    case 403:
      return {
        message:
          'You do not have access to this Lens. Ask a reporting admin or someone with access to share it with you.',
        canRetry: false,
      };
    case 404:
      return { message: 'This Lens does not exist. It may have been deleted.', canRetry: false };
    default:
      // Includes a missing status: a request that failed before it got a response is the case a
      // retry is actually for.
      return { message: 'We couldn’t load this Lens. Check your connection and try again.', canRetry: true };
  }
}
